import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { Receipt } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#ec4899'];

export default function PurchasesSummaryReportPage() {
  const [data, setData] = useState(null);
  const f = useReportFilters({ withUnassigned: true });
  const dateFilter = f.range;
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (f.branchIds.length) params.branch_ids = f.branchIds.join(',');
      const res = await api.get('/accounting/purchases-summary-report', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [f.params]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="purchases-summary-report-page">
        <ReportHeader icon={Receipt} title="Purchases Summary" subtitle="Aggregated purchases by category, branch and supplier" testId="psum-header" />
        <ReportFilterBar testId="psum-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds}
          range={f.range} onRange={f.setRange}>
          <ExportTrio spec={() => ({ title: 'Purchases Summary', subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'purchases_summary', rows: [...(data?.by_branch || []).map((r) => ({ ...r, section: 'Branch' })), ...(data?.by_category || []).map((r) => ({ ...r, section: 'Category' })), ...(data?.by_supplier || []).map((r) => ({ ...r, section: 'Supplier' }))], cols: [{ key: 'section', label: 'Section' }, { key: 'name', label: 'Name' }, { key: 'total', label: 'Total', numeric: true }] })} disabled={!data} testId="psum-export" />
        </ReportFilterBar>

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="border-error/30 bg-error/10"><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-error font-semibold">Total Purchases</p><p className="text-2xl font-bold text-error">{cur()} {data.total_purchases?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cash</p><p className="text-2xl font-bold text-success">{cur()} {data.by_payment_mode?.cash?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Bank</p><p className="text-2xl font-bold text-info">{cur()} {data.by_payment_mode?.bank?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Credit</p><p className="text-2xl font-bold text-warning">{cur()} {data.by_payment_mode?.credit?.toLocaleString()}</p></CardContent></Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {data.by_category?.length > 0 && (
                <Card><CardContent className="pt-5">
                  <h3 className="font-semibold mb-4">By Category</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.by_category.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                      <Bar dataKey="total" fill="#ef4444" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent></Card>
              )}
              {data.by_supplier?.length > 0 && (
                <Card><CardContent className="pt-5">
                  <h3 className="font-semibold mb-4">Top Suppliers</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={data.by_supplier.slice(0, 8)} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name.slice(0, 12)} ${(percent * 100).toFixed(0)}%`}>
                        {data.by_supplier.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent></Card>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 bg-muted/50 border-b font-semibold text-sm">By Category</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {data.by_category?.map((c, i) => (
                          <tr key={i} className="border-b hover:bg-muted/50">
                            <td className="px-4 py-2.5 font-medium capitalize">{c.name?.replace('_', ' ')}</td>
                            <td className="px-4 py-2.5 text-right font-bold text-error">{cur()} {c.total?.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-right text-muted-foreground text-xs">{data.total_purchases > 0 ? ((c.total / data.total_purchases) * 100).toFixed(1) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 bg-muted/50 border-b font-semibold text-sm">By Branch</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {data.by_branch?.map((b, i) => (
                          <tr key={i} className="border-b hover:bg-muted/50">
                            <td className="px-4 py-2.5 font-medium">{b.name}</td>
                            <td className="px-4 py-2.5 text-right font-bold text-error">{cur()} {b.total?.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-right text-muted-foreground text-xs">{data.total_purchases > 0 ? ((b.total / data.total_purchases) * 100).toFixed(1) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
        {loading && <div className="text-center py-12 text-muted-foreground">Loading purchases summary...</div>}
      </div>
    </DashboardLayout>
  );
}
